import { z } from 'zod';

/**
 * Auth form schemas; the wire is validated by `@keboola/api-client/developerPortal`.
 *
 * - Password strength duplicates Cognito's rule so users learn it without a round-trip.
 */

const email = z.string().min(1, 'Required').pipe(z.email('Enter a valid email address'));

const requiredString = z.string().min(1, 'Required');

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}/;
const PASSWORD_MESSAGE =
  'Must contain at least 8 characters and at least one lowercase letter, one uppercase letter and one number.';

const password = z.string().min(1, 'Required').regex(PASSWORD_PATTERN, PASSWORD_MESSAGE);

export const loginFormSchema = z.object({
  email,
  password: requiredString,
});

/**
 * Verification-code length: the input's slot count and the schema's rule.
 *
 * - Cognito issues six digits on both `SMS_MFA` and `SOFTWARE_TOKEN_MFA`.
 * - `login.tsx` reads it, so slot count and rule cannot drift.
 */
export const MFA_CODE_LENGTH = 6;

export const mfaFormSchema = z.object({
  // A fixed-slot OTP, so a short entry is unfinished. Wording stays channel-neutral (SMS too).
  code: requiredString
    .regex(/^\d+$/, 'Enter the digits of your verification code')
    .length(MFA_CODE_LENGTH, `Enter all ${MFA_CODE_LENGTH} digits of your verification code`),
});

export const signUpFormSchema = z
  .object({
    name: requiredString,
    email,
    password,
    confirmPassword: requiredString,
  })
  // On `confirmPassword`, not the form root, so the message lands under that field.
  .refine((values) => values.password === values.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

export const resetRequestFormSchema = z.object({ email });

export const resetConfirmFormSchema = z
  .object({
    code: requiredString,
    password,
    confirmPassword: requiredString,
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });
